import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../utils/api';

// Admin backoffice for the client invoicing portal (www.slf-accounting.co/portal).
// Create / reset / disable / delete portal logins; documents & merchant data always survive.

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color,
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
});
// 11 → 12px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
// Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this:
// the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular weight, where
// they were light grey at semibold (600), 10px above and below where it was 8, on one 16px line, on white, over the
// darker --border-strong line — a 37px band, as every list's is.
const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-surface)' };
const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '8px 10px', borderBottom: '1px solid var(--border)' };

const PORTAL_URL = 'https://www.slf-accounting.co/portal';

function PasswordModal({ email, password, onClose }: { email: string; password: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const text = `Sign in: ${PORTAL_URL}\nEmail: ${email}\nTemporary password: ${password}`;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>One-time password</div>
        <div style={{ fontSize: 12.5, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
          Shown only once — it is not stored anywhere. The client must change it on first sign-in.
        </div>
        <pre style={{ fontSize: 13, background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>{text}</pre>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={() => { navigator.clipboard.writeText(text).then(() => setCopied(true)); }} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
          <button onClick={onClose} style={btn('var(--accent)')}>Done</button>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (email: string, password: string, added?: boolean) => void }) {
  const [clients, setClients] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [clientId, setClientId] = useState('');
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.adminPortalClients().then((r: any) => setClients(r.rows || [])).catch((e: any) => setErr(e.message)); }, []);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return clients.filter(c => !needle || `${c.companyName} ${c.clientName} ${c.email}`.toLowerCase().includes(needle)).slice(0, 60);
  }, [clients, q]);
  const pick = (c: any) => { setClientId(c.id); setEmail(c.email || ''); };
  const picked = clients.find(c => c.id === clientId);
  const create = async () => {
    setErr(''); setBusy(true);
    try {
      const r: any = await api.adminPortalCreate(clientId, email.trim());
      // No password comes back when the address already had a portal and this company simply joined
      // it — the owner keeps the password they already use. Showing a password modal there would be
      // a lie, so say what actually happened instead.
      onCreated(email.trim() || (picked?.email || ''), r.password, !!r.added);
    } catch (e: any) { setErr(e.message); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 560, maxHeight: '86vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>New portal account</div>
        {!clientId ? (
          <>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search client…" style={inputStyle} />
            <div style={{ flex: 1, overflowY: 'auto', marginTop: 10, border: '1px solid var(--border)', borderRadius: 8 }}>
              {filtered.map(c => (
                <div key={c.id} onClick={() => pick(c)}
                  style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.companyName || c.clientName}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{c.email || 'no email on record'}</div>
                  </div>
                  {/* Not a block — a company can have several people signing in, each with their own
                      password. This just says so out loud before staff add another. */}
                  {!!c.hasAccount && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {c.loginCount > 1 ? `${c.loginCount} logins` : '1 login'} — adding another is fine
                  </span>}
                </div>
              ))}
              {filtered.length === 0 && <div style={{ padding: 16, fontSize: 12.5, color: 'var(--text-tertiary)' }}>No matches.</div>}
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>{picked?.companyName || picked?.clientName}
              <button onClick={() => setClientId('')} style={{ marginLeft: 10, border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12 }}>change</button>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Login email</label>
              <input value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} placeholder="client@example.com" />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              The merchant profile (name, tax ID, address, VAT status) is pre-filled from the client record.
              A one-time password is generated and shown to you next.
            </div>
          </div>
        )}
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>Cancel</button>
          {!!clientId && <button onClick={create} disabled={busy || !email.trim()} style={{ ...btn('var(--accent)'), opacity: busy || !email.trim() ? .6 : 1 }}>{busy ? 'Creating…' : 'Create account'}</button>}
        </div>
      </div>
    </div>
  );
}

export default function PortalClients() {
  const [rows, setRows] = useState<any[]>([]);
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [pwModal, setPwModal] = useState<{ email: string; password: string } | null>(null);
  const [notice, setNotice] = useState('');
  const unlink = async (r: any, x: any) => {
    if (!window.confirm(`Remove ${x.name} from ${r.email}?\n\nThey lose access to that company immediately. Its documents are untouched.`)) return;
    setBusy(true);
    try { await api.adminPortalUnlinkCompany(r.userId, x.clientId); load(); }
    catch (e: any) { window.alert(e?.message || 'Could not remove it.'); }
    finally { setBusy(false); }
  };
  const [busy, setBusy] = useState(false);

  const load = () => api.adminPortalAccounts().then((r: any) => setRows(r.rows || [])).catch((e: any) => setErr(e.message));
  useEffect(() => { load(); }, []);

  const resetPw = async (r: any) => {
    if (!window.confirm(`Reset the portal password for ${r.email}? The old password stops working immediately.`)) return;
    setBusy(true);
    try { const res: any = await api.adminPortalResetPw(r.userId); setPwModal({ email: r.email, password: res.password }); }
    catch (e: any) { window.alert(e.message); } finally { setBusy(false); }
  };
  const toggleActive = async (r: any) => {
    setBusy(true);
    try { await api.adminPortalSetActive(r.userId, !r.active); load(); }
    catch (e: any) { window.alert(e.message); } finally { setBusy(false); }
  };
  const del = async (r: any) => {
    if (!window.confirm(`Delete the portal login ${r.email}?\n\nIssued documents and numbering are kept — only the login is removed.`)) return;
    setBusy(true);
    try { await api.adminPortalDelete(r.userId); load(); }
    catch (e: any) { window.alert(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          Client logins for the invoicing portal at <a href={PORTAL_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{PORTAL_URL}</a>.
          Telegram-approved registrations create their account automatically.
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowCreate(true)} style={btn('var(--accent)')}>
          <i className="ti ti-plus" style={{ fontSize: 13, marginRight: 6 }} aria-hidden />New portal account
        </button>
      </div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>{err}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <thead><tr>
          <th style={th}>Client</th><th style={th}>Login email</th><th style={th}>VAT</th>
          <th style={th}>Docs (INV/QO/RE)</th><th style={th}>Disclaimer</th><th style={th}>Status</th><th style={th}></th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.userId} style={{ opacity: r.active ? 1 : 0.55 }}>
              <td style={td}>
                <div style={{ fontWeight: 600 }}>{r.companyName || r.clientName || '—'}</div>
                {!!r.trialUntil && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>trial until {r.trialUntil}</div>}
                {/* Companies this login reaches BEYOND its own. Listed here so "what can this person
                    see?" is answerable from the list. Removing one withdraws access only — the
                    documents belong to the company. */}
                {(r.extraCompanies || []).map((x: any) => (
                  <div key={x.clientId} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                    <span>+ {x.name || x.clientId}</span>
                    <button onClick={() => unlink(r, x)} disabled={busy} title="Remove this company from the login"
                      style={{ border: 'none', background: 'none', color: '#B45309', cursor: 'pointer', padding: 0, fontSize: 11.5 }}>remove</button>
                  </div>
                ))}
              </td>
              <td style={td}>{r.email}{!!r.mustChange && <div style={{ fontSize: 10.5, color: '#B45309' }}>temp password not yet changed</div>}</td>
              <td style={td}>{r.vatRegistered ? 'VAT' : '—'}</td>
              <td style={td}>{r.merchantId ? `${r.invoices} / ${r.quotations} / ${r.receipts}` : 'no profile'}</td>
              <td style={td}>{r.disclaimerAt ? '✓ accepted' : 'pending'}</td>
              <td style={td}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: r.active ? '#E4F5EC' : '#FCEBEB', color: r.active ? '#1D9E75' : '#E24B4A' }}>
                  {r.active ? 'Active' : 'Disabled'}
                </span>
              </td>
              <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                <button onClick={() => resetPw(r)} disabled={busy} title="Reset password" style={{ border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', marginRight: 8 }}><i className="ti ti-key" /></button>
                <button onClick={() => toggleActive(r)} disabled={busy} title={r.active ? 'Disable login' : 'Enable login'} style={{ border: 'none', background: 'none', color: r.active ? '#B45309' : '#1D9E75', cursor: 'pointer', marginRight: 8 }}><i className={`ti ${r.active ? 'ti-lock' : 'ti-lock-open'}`} /></button>
                <button onClick={() => del(r)} disabled={busy} title="Delete login" style={{ border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer' }}><i className="ti ti-trash" /></button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={td} colSpan={7}>No portal accounts yet.</td></tr>}
        </tbody>
      </table>
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(email, password, added) => {
            setShowCreate(false);
            if (added) setNotice(`${email} already had a portal — this company was added to it. The owner signs in with their existing password.`);
            else setPwModal({ email, password });
            load();
          }}
        />
      )}
      {!!notice && (
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: '#E8F3F0',
                      border: '1px solid #C5E3DA', color: '#0E7C66', fontSize: 13 }}>
          {notice}
          <button onClick={() => setNotice('')} style={{ border: 'none', background: 'none', color: '#0E7C66', cursor: 'pointer', float: 'right' }}>✕</button>
        </div>
      )}
      {pwModal && <PasswordModal email={pwModal.email} password={pwModal.password} onClose={() => setPwModal(null)} />}
    </div>
  );
}
